/**
 * Frontend mirror of src/chat/chat-tool-category-registry.ts.
 *
 * Every picker that scopes an AI surface to tool categories — the chat tool toggles,
 * persona and task editors, A2A config, MCP servers — reads this list instead of
 * carrying its own. Keep the rows in sync with the backend registry; the backend's
 * invariant test guards the backend side, and anything the UI shows that the backend
 * rejects surfaces immediately as a failed save.
 *
 * Categories arrive from the API as plain strings, so the helpers here take `string`
 * and are total: an unknown value (a legacy persisted category) gets its raw name as
 * a label rather than being dropped.
 */

export type ChatToolCategory =
  | 'Built-in'
  | 'Interactive'
  | 'Documentation'
  | 'Elasticsearch'
  | 'Code Search'
  | 'Confluence'
  | 'Jira'
  | 'Slack'
  | 'Cloudflare'
  | 'Salesforce'
  | 'HubSpot'
  | 'Web Search'
  | 'Grafana'
  | 'Accounts'
  | 'Looker'
  | 'Marketing Site Content'
  | 'Kibo Tenant'
  | 'RFP'
  | 'Files'
  | 'Security Watch';

interface ChatToolCategoryUiDefinition {
  /** Humanized label shown on toggles, checkboxes and chips. */
  label: string;
  /** One-line explanation, used as a title/tooltip. */
  description: string;
  /** Feature flag key gating the category, if any. */
  featureFlag?: string;
  /** Set when the category is wired by the surface (or granted by a managed role) rather than chosen by the user. */
  alwaysOn?: 'builtin' | 'interactive' | 'managedRole';
  /** Pre-checked in the chat Custom tool picker. */
  defaultOn?: boolean;
  /**
   * Builds no tools unless a human is present to answer a blocking prompt. Pickers
   * for automated surfaces leave it out — ticking it there is a silent no-op.
   */
  interactiveOnly?: boolean;
  /** Selectable on an MCP server's allowlist. */
  mcpEligible: boolean;
}

export const CHAT_TOOL_CATEGORY_UI: Record<ChatToolCategory, ChatToolCategoryUiDefinition> = {
  'Built-in': {
    label: 'Built-in',
    description: 'Always-on tools every chat gets: chat history and transcript reading.',
    alwaysOn: 'builtin',
    mcpEligible: false,
  },
  Interactive: {
    label: 'Interactive',
    description: 'Blocking prompts that ask you to type an answer or pick an option.',
    alwaysOn: 'interactive',
    mcpEligible: false,
  },
  Documentation: {
    label: 'Documentation',
    description: 'Search and read the Kibo platform documentation.',
    defaultOn: true,
    mcpEligible: true,
  },
  Elasticsearch: {
    label: 'Elasticsearch',
    description: 'Query and analyze Elasticsearch logs.',
    mcpEligible: true,
  },
  'Code Search': {
    label: 'Code Search',
    description: 'Search, read and list files across synced code repositories.',
    defaultOn: true,
    mcpEligible: true,
  },
  Confluence: {
    label: 'Confluence',
    description: 'Search and read Confluence pages.',
    mcpEligible: true,
  },
  Jira: {
    label: 'Jira',
    description: 'Search and read Jira issues.',
    mcpEligible: true,
  },
  Slack: {
    label: 'Slack',
    description: 'Search synced Slack messages, threads and channel history.',
    mcpEligible: true,
  },
  Cloudflare: {
    label: 'Cloudflare',
    description: 'Resolve DNS and query the Cloudflare API.',
    mcpEligible: true,
  },
  Salesforce: {
    label: 'Salesforce',
    description: 'Search Salesforce cases and run SOQL queries.',
    mcpEligible: true,
  },
  HubSpot: {
    label: 'HubSpot',
    description: 'Search the HubSpot API spec and read CRM data.',
    mcpEligible: true,
  },
  'Web Search': {
    label: 'Web Search',
    description: 'Search the public web and scrape pages as Markdown.',
    mcpEligible: true,
  },
  Grafana: {
    label: 'Grafana',
    description: 'Explore Grafana dashboards, datasources and run read-only queries.',
    mcpEligible: true,
  },
  Accounts: {
    label: 'Accounts',
    description: 'Search the customer account database and read account detail.',
    defaultOn: true,
    mcpEligible: true,
  },
  Looker: {
    label: 'Looker',
    description: "Ask natural-language questions about a tenant's order data via Looker.",
    mcpEligible: true,
  },
  'Marketing Site Content': {
    label: 'Marketing Site Content',
    description: "Search Kibo's marketing site — blog posts and marketing pages.",
    mcpEligible: true,
  },
  'Kibo Tenant': {
    label: 'Kibo Tenant',
    description: "Delegated, per-user access to a live customer tenant's API.",
    featureFlag: 'KIBO_TENANT_ACCESS',
    // Human-in-the-loop end to end: access starts with a blocking approval and
    // every write is confirmed per call, so on an automated surface it builds
    // nothing at all.
    interactiveOnly: true,
    mcpEligible: false,
  },
  RFP: {
    label: 'RFP',
    description: 'Search the RFP knowledge base of Q&A pairs and source documents.',
    mcpEligible: true,
  },
  Files: {
    label: 'Files',
    description: "List, read and search the files attached to this chat, the documents and decks the assistant authors in it, and your own library.",
    defaultOn: false,
    // Eligible since /library belongs to the user rather than to a conversation,
    // so the tools have a real filesystem even where the MCP bridge has no thread.
    mcpEligible: true,
  },
  'Security Watch': {
    label: 'Security Watch',
    description: 'Read, investigate and triage Security Watch findings. Granted by the security-watch role, in a private chat or over MCP; never picked.',
    alwaysOn: 'managedRole',
    featureFlag: 'SECURITY_WATCH',
    mcpEligible: true,
  },
};

/** Declaration order — used wherever categories are listed in the UI. */
export const CHAT_TOOL_CATEGORY_UI_ORDER = Object.keys(CHAT_TOOL_CATEGORY_UI) as ChatToolCategory[];

export function isKnownToolCategory(category: string): category is ChatToolCategory {
  return Object.prototype.hasOwnProperty.call(CHAT_TOOL_CATEGORY_UI, category);
}

/**
 * Categories a user can pick. Excludes the ones the surface wires itself
 * (Built-in, Interactive), which have no toggle.
 */
export const SELECTABLE_UI_CATEGORIES: ChatToolCategory[] = CHAT_TOOL_CATEGORY_UI_ORDER.filter(
  (c) => CHAT_TOOL_CATEGORY_UI[c].alwaysOn === undefined,
);

/** Selectable categories that can be exposed over MCP. */
export const MCP_ELIGIBLE_UI_CATEGORIES: ChatToolCategory[] = SELECTABLE_UI_CATEGORIES.filter(
  (c) => CHAT_TOOL_CATEGORY_UI[c].mcpEligible,
);

/** Categories pre-checked in the chat Custom tool picker. */
export const DEFAULT_ON_UI_CATEGORIES: ChatToolCategory[] = SELECTABLE_UI_CATEGORIES.filter(
  (c) => CHAT_TOOL_CATEGORY_UI[c].defaultOn === true,
);

/**
 * Categories that build tools without a human present — what a picker for an
 * automated surface (the AI triage agent's allowlist) offers. Mirrors
 * NON_INTERACTIVE_CATEGORIES.
 */
export const NON_INTERACTIVE_UI_CATEGORIES: ChatToolCategory[] = SELECTABLE_UI_CATEGORIES.filter(
  (c) => CHAT_TOOL_CATEGORY_UI[c].interactiveOnly !== true,
);

/** Total: unknown (legacy persisted) categories keep their raw name. */
export function chatToolCategoryUiLabel(category: string): string {
  return isKnownToolCategory(category) ? CHAT_TOOL_CATEGORY_UI[category].label : category;
}

/** Total: unknown categories have no description. */
export function chatToolCategoryUiDescription(category: string): string | undefined {
  return isKnownToolCategory(category) ? CHAT_TOOL_CATEGORY_UI[category].description : undefined;
}

/**
 * The feature flag gating a category, if any. Unknown categories are ungated —
 * the backend is the authority, and hiding a legacy value would make it invisible
 * rather than editable.
 */
export function chatToolCategoryFeatureFlag(category: string): string | undefined {
  return isKnownToolCategory(category) ? CHAT_TOOL_CATEGORY_UI[category].featureFlag : undefined;
}
